/**
 * Application-owned capability catalog for expressive presets.
 * Provider prompts and UI visibility should derive from this document.
 *
 * Sequence ≠ MIDI arpeggiator: Vital presets do not emit MIDI or reorder held
 * chords. "Sequence" means tempo-synced modulators that change pitch, gate,
 * filter, or other destinations while a note is held inside the synth.
 */
export const PRESET_SCHEMA_VERSION = 2;
export const SEQUENCE_CAPABILITY_NOTE = "Sequence is an arp-like held-note pattern owned by the .vital file. " +
    "It does not generate MIDI, reorder chords, or act as a DAW arpeggiator.";
export const PRESET_CAPABILITIES = {
    schemaVersion: PRESET_SCHEMA_VERSION,
    sequenceSemantics: SEQUENCE_CAPABILITY_NOTE,
    unsupportedClaims: [
        "MIDI note/chord generation or reordering",
        "Standalone DAW-style arpeggiator transport control",
        "Sample import or factory preset/wavetable reuse",
        "Arbitrary raw Vital JSON authoring by the model",
    ],
    limits: {
        maxOps: 400,
        maxModulations: 24,
        maxCustomPoints: 64,
        minCustomPoints: 2,
        maxStepValues: 32,
        maxSequenceSteps: 32,
        maxSequenceLanes: 4,
        maxModulators: 8,
        maxMacros: 4,
        maxUnisonVoicesTotal: 24,
    },
    capabilities: [
        {
            id: "voice.polyphony",
            label: "Polyphony",
            description: "How many notes may overlap.",
            guaranteed: true,
            vitalDependent: false,
        },
        {
            id: "voice.mono_legato",
            label: "Mono / legato",
            description: "Single-voice lines with optional legato note connection.",
            guaranteed: true,
            vitalDependent: false,
        },
        {
            id: "voice.glide",
            label: "Glide",
            description: "Portamento time between notes.",
            guaranteed: true,
            vitalDependent: false,
        },
        {
            id: "voice.transpose",
            label: "Voice transpose",
            description: "Global pitch offset; preferred destination for pitch sequences.",
            guaranteed: true,
            vitalDependent: false,
        },
        {
            id: "oscillator.pan_tune_phase",
            label: "Oscillator pan / tune / phase",
            description: "Stereo placement, fine tune, phase, and random phase.",
            guaranteed: true,
            vitalDependent: false,
        },
        {
            id: "oscillator.unison_spread",
            label: "Unison spread",
            description: "Unison blend, stereo spread, and frame spread.",
            guaranteed: true,
            vitalDependent: true,
        },
        {
            id: "oscillator.routing",
            label: "Oscillator filter routing",
            description: "Which oscillators feed the main filter.",
            guaranteed: true,
            vitalDependent: false,
        },
        {
            id: "oscillator.sub",
            label: "Sub oscillator",
            description: "Dedicated sub waveform, level, transpose, and pan.",
            guaranteed: true,
            vitalDependent: false,
        },
        {
            id: "oscillator.morph_distortion",
            label: "Spectral morph / phase distortion",
            description: "Selected morph and phase-distortion amount pairs.",
            guaranteed: true,
            vitalDependent: true,
        },
        {
            id: "envelope.multi",
            label: "Multiple envelopes",
            description: "Env 1 is amplitude; env 2–4 are modulation sources.",
            guaranteed: true,
            vitalDependent: false,
        },
        {
            id: "envelope.delay_hold_curve",
            label: "Envelope delay / hold / curves",
            description: "Delay, hold, and attack/decay/release curve shaping.",
            guaranteed: true,
            vitalDependent: true,
        },
        {
            id: "filter.model_drive_tracking",
            label: "Filter character",
            description: "Filter model/style, drive, key tracking, and mix.",
            guaranteed: true,
            vitalDependent: true,
        },
        {
            id: "filter.routing",
            label: "Filter routing",
            description: "Typed oscillator/sample inputs into the main filter.",
            guaranteed: true,
            vitalDependent: false,
        },
        {
            id: "modulator.preset_shapes",
            label: "Preset LFO shapes",
            description: "Sine, triangle, square, saw, random, sample-and-hold.",
            guaranteed: true,
            vitalDependent: false,
        },
        {
            id: "modulator.custom_points",
            label: "Custom modulator curves",
            description: "Bounded custom point arrays with optional smoothing.",
            guaranteed: true,
            vitalDependent: false,
        },
        {
            id: "modulator.steps",
            label: "Stepped modulators",
            description: "1–32 stepped values for gates and rhythmic motion.",
            guaranteed: true,
            vitalDependent: false,
        },
        {
            id: "modulator.tempo_sync",
            label: "Tempo-synced modulators",
            description: "Allowlisted note divisions instead of free Hz.",
            guaranteed: true,
            vitalDependent: false,
        },
        {
            id: "modulator.trigger_phase",
            label: "Modulator trigger and phase",
            description: "Note retrigger, sync, free, one-shot, phase, fade, delay, stereo.",
            guaranteed: true,
            vitalDependent: true,
        },
        {
            id: "sequence.pitch_gate",
            label: "Pitch / gate sequence",
            description: "High-level held-note sequence compiled into stepped modulators and routes. " +
                SEQUENCE_CAPABILITY_NOTE,
            guaranteed: true,
            vitalDependent: false,
            compilerNotes: "Pitch routes to voice_transpose; gate routes to volume or osc levels.",
        },
        {
            id: "sequence.timbral_lanes",
            label: "Sequence timbral lanes",
            description: "Optional parallel lanes for cutoff, wavetable position, pan, or effects.",
            guaranteed: true,
            vitalDependent: false,
        },
        {
            id: "modulation.matrix",
            label: "Modulation matrix",
            description: "Bounded source→destination routes with amount and bipolar.",
            guaranteed: true,
            vitalDependent: false,
        },
        {
            id: "modulation.macros",
            label: "Named macros",
            description: "Up to four named macros with typed destination assignments.",
            guaranteed: true,
            vitalDependent: false,
        },
        {
            id: "effects.core",
            label: "Core effects",
            description: "Chorus, delay, reverb, phaser, flanger with musical parameters.",
            guaranteed: true,
            vitalDependent: true,
        },
        {
            id: "effects.distortion_compression",
            label: "Distortion and compression",
            description: "Drive/mix distortion and compressor density controls.",
            guaranteed: true,
            vitalDependent: true,
        },
        {
            id: "wavetable.generators",
            label: "Wavetable generators",
            description: "Deterministic basic, pulse, and harmonic wavetable synthesis.",
            guaranteed: true,
            vitalDependent: false,
        },
    ],
};
export function capabilityById(id) {
    return PRESET_CAPABILITIES.capabilities.find((c) => c.id === id);
}
export function capabilityIds() {
    return PRESET_CAPABILITIES.capabilities.map((c) => c.id);
}
//# sourceMappingURL=capabilities.js.map